import React, { useCallback, useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';

import biService from '../../../service/bi.service';
import { BiChartType, BiReportType } from '../../../config/bi_report';
import DrillSelectionPath from './DrillSelectionPath';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

const DEFAULT_BORDER_COLORS = [
  'rgb(54, 162, 235)',
  'rgb(255, 99, 132)',
  'rgb(255, 159, 64)',
  'rgb(255, 205, 86)',
  'rgb(75, 192, 192)',
  'rgb(153, 102, 255)',
  'rgb(201, 203, 207)',
];
const DEFAULT_BACKGROUND_COLORS = DEFAULT_BORDER_COLORS.map((color) =>
  color.replace('rgb(', 'rgba(').replace(')', ', 0.5)')
);

const BarReport = ({ report, linkedReportIdState, activeLevelState, filterValsState, reportStatesState, height }) => {
  const [chartData, setChartData] = useState({});
  const [chartHeight, setChartHeight] = useState(650);

  const { activeLevel, setActiveLevel } = activeLevelState;
  const { filterVals, setFilterVals } = filterValsState;
  const { linkedReportId, setLinkedReportId } = linkedReportIdState;
  const { reportStates, setReportStates } = reportStatesState;

  const getChartData = useCallback(() => {
    biService.getReportData(report.id, activeLevel, filterVals).then((data) => setChartData(data));
  }, [report.id, activeLevel, filterVals]);

  useEffect(() => {
    const prevReport = reportStates[reportStates.length - 1];
    if (Object.keys(report).length > 0 && prevReport?.reportId !== report.id) {
      getChartData();
    }
  }, [report, getChartData, activeLevel, reportStates]);

  useEffect(() => {
    if (height) setChartHeight(height);
  }, [height]);

  const hasDrillSelections = (filterVals?.filter ?? []).some((item) => !item?.isDefault);

  const handleResetDrill = useCallback(() => {
    setFilterVals((prev) => {
      const defaultFilters = (prev?.filter ?? []).filter((item) => item?.isDefault);
      return { ...prev, filter: defaultFilters };
    });
    setActiveLevel(null);
  }, [setFilterVals, setActiveLevel]);

  const handleSelectDrillLevel = useCallback(
    (targetIndex) => {
      setFilterVals((prev) => {
        const defaultFilters = (prev?.filter ?? []).filter((item) => item?.isDefault);
        const drillFilters = (prev?.filter ?? []).filter((item) => !item?.isDefault);
        const nextDrillFilters = drillFilters.slice(0, targetIndex + 1);
        return { ...prev, filter: [...defaultFilters, ...nextDrillFilters] };
      });
      setActiveLevel((prev) => (prev === null ? null : targetIndex));
    },
    [setFilterVals, setActiveLevel]
  );

  const chartFields = report.biField?.filter((field) => !field.isGroup && !field.isFilter) ?? [];
  const data = {
    labels: chartData.label,
    datasets: chartData?.chart
      ? chartData.chart.map((c, index) => {
          const chartType = c.chartType ?? chartFields[index]?.chartType;
          const color = chartFields[index]?.color;
          return {
            ...c,
            ...(report.reportType === BiReportType.COMBO && {
              type: chartType === BiChartType.LINE ? 'line' : 'bar',
            }),
            backgroundColor: color ?? DEFAULT_BACKGROUND_COLORS[index % DEFAULT_BACKGROUND_COLORS.length],
            borderColor: color ?? DEFAULT_BORDER_COLORS[index % DEFAULT_BORDER_COLORS.length],
            borderWidth: 1,
            hoverOffset: 4,
          };
        })
      : [],
  };
  const ChartComponent = report.reportType === BiReportType.LINE ? Line : Bar;

  const options = {
    ...(report.reportType === BiReportType.HORIZONTAL && { indexAxis: 'y' }),
    maintainAspectRatio: false,
    aspectRatio: 1,
    plugins: {
      colors: {
        enabled: false,
      },
      title: {
        display: false,
        text: report.name,
      },
    },
    onClick: (event, elements) => {
      //@TODO majd egy központi fg jó lenne a click eseményekre
      if (elements.length > 0) {
        const { biDrillingLink, biDrilling, biField, id } = report;

        const label = data.labels[elements[0].index];
        const maxLevel = biDrilling.length > 0 ? Math.max(...biDrilling.map((drill) => drill.level)) : null;
        let actLevel = activeLevel !== null ? activeLevel + 1 : 0;

        let filterField;

        if (actLevel === 0 || actLevel === null) {
          filterField = biField.find((f) => f.isGroup)?.field;
        } else {
          filterField = biDrilling?.find((f) => {
            return f.level === activeLevel;
          })?.field;
        }

        const newFilter = { ...filterVals };

        if (maxLevel + 1 === actLevel || (biDrillingLink && maxLevel === null)) {
          if (biDrillingLink) {
            setReportStates((prev) => {
              prev.push({
                reportId: id,
                linkedReportId: linkedReportId,
                filter: { filter: newFilter.filter.map((data) => data) },
                activeLevel,
              });
              return prev;
            });
            setLinkedReportId(biDrillingLink.linkedReport);

            newFilter.filter.push({ field: filterField, value: label });

            const updatedFilter = newFilter.filter.map((item) => {
              const newReportField = biDrillingLink.biDrillingLinkField.find(
                (linkField) => linkField.parentField === item.field
              );
              if (newReportField) {
                return { ...item, field: newReportField.childField };
              }
              return item;
            });

            setFilterVals({ filter: updatedFilter });
            setActiveLevel(null);
          } else {
            return;
          }
        } else {
          newFilter.filter.push({ field: filterField, value: label });
          if (!biDrilling.length > 0) {
            return;
          }
          setActiveLevel(actLevel);
          setFilterVals(newFilter);
        }
      }
    },
  };

  return (
    <>
      <div className="flex flex-col items-center mt-2">
        <button
          type="button"
          onClick={handleResetDrill}
          disabled={!hasDrillSelections}
          className={`${hasDrillSelections ? 'cursor-pointer hover:underline' : 'cursor-default'}`}
        >
          <Typography variant="h6" sx={{ color: 'text.primary' }}>
            {report.name}
          </Typography>
        </button>
        <DrillSelectionPath filterVals={filterVals} onSelectLevel={handleSelectDrillLevel} />
      </div>
      <div className="mx-2 flex justify-center" style={{ height: `${chartHeight}px` }}>
        <ChartComponent data={data} options={options} />
      </div>
    </>
  );
};

export default BarReport;
